<template>
  <div>
    <div class="page-header"><div><h1 class="page-title">{{ t('portal.navProjects') }}</h1></div></div>

    <div class="portal-search">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      <input v-model="query" class="form-control" :placeholder="t('common.search')" />
    </div>

    <div v-if="loading" class="spinner"></div>
    <div v-else-if="projects.length === 0" class="empty-state"><p>{{ t('portal.noProjects') }}</p></div>
    <div v-else-if="filtered.length === 0" class="empty-state"><p>{{ t('search.noResults') }} “{{ query }}”</p></div>
    <div v-else class="proj-list">
      <router-link v-for="p in filtered" :key="p.id" :to="'/portal/projects/' + p.id" class="proj-card">
        <span class="proj-dot" :style="{ background: p.color || 'var(--border-2)' }"></span>
        <div class="proj-body">
          <div class="proj-name">{{ p.name }}</div>
          <div class="proj-meta">{{ fmtMoney(projectTotal(p), p.currency) }}</div>
        </div>
        <span class="proj-count">{{ p.task_count }}</span>
        <span class="proj-go">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        </span>
      </router-link>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'
import { fmtMoney, projectTotal } from '@/utils/billing'

export default {
  name: 'ClientProjects',
  setup() {
    const { t } = useI18n()
    const route = useRoute()
    const projects = ref([])
    const loading = ref(true)
    // Seed from the topbar search (?q=) and keep in sync if it changes.
    const query = ref((route.query.q || '').toString())
    watch(() => route.query.q, v => { query.value = (v || '').toString() })

    const filtered = computed(() => {
      const term = query.value.trim().toLowerCase()
      if (!term) return projects.value
      return projects.value.filter(p => (p.name || '').toLowerCase().includes(term))
    })

    onMounted(async () => {
      try { projects.value = (await api.getProjects()).data || [] } catch {} finally { loading.value = false }
    })
    return { t, projects, filtered, query, loading, fmtMoney, projectTotal }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; }
.portal-search {
  display: flex; align-items: center; gap: 8px;
  max-width: 360px; margin-bottom: 16px;
  padding: 0 12px; border: 1px solid var(--card-brd, var(--border)); border-radius: var(--radius-sm, 8px);
  background: var(--surface); color: var(--muted);
}
.portal-search input { border: none; background: none; padding: 9px 0; flex: 1; color: var(--text); font: inherit; }
.portal-search input:focus { outline: none; }
/* Project boxes — same visual language as the internal tasks board cards:
   16px radius, soft shadow, bold name, red count pill. */
.proj-list { display: flex; flex-direction: column; gap: 14px; }
.proj-card {
  display: flex; align-items: center; gap: 11px;
  background: var(--surface); border: 1.5px solid var(--card-brd);
  border-radius: 16px; box-shadow: 0 2px 12px rgba(20,24,40,.07);
  padding: 14px 18px; text-decoration: none; color: var(--text);
  transition: box-shadow .12s, transform .12s;
}
.proj-card:hover { box-shadow: 0 4px 18px rgba(20,24,40,.12); transform: translateY(-1px); }
.proj-dot { width: 11px; height: 11px; border-radius: 4px; flex-shrink: 0; }
.proj-body { flex: 1; min-width: 0; display: flex; align-items: baseline; gap: 12px; }
.proj-name { font-size: var(--fs-h4); font-weight: 800; letter-spacing: -.01em; }
.proj-meta { font-size: 12px; color: var(--muted); }
.proj-count {
  min-width: 22px; height: 22px; padding: 0 6px;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--danger); color: #fff;
  border-radius: 999px; font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums;
}
.proj-go { color: var(--muted); display: inline-flex; }
[dir='ltr'] .proj-go svg { transform: scaleX(-1); }
</style>
